/**
 * Bidirectional `__crucibleInspect` postMessage protocol for the
 * unified select / pause mode (select-to-chat,
 * `docs/design/preview-select-to-chat-prd.md`).
 *
 * Topology (no created-game code involved):
 *   - Electron MAIN injects the inspector script into EVERY cross-origin
 *     preview iframe (TV + every controller) via
 *     `webFrameMain.executeJavaScript` — the only actor that can reach a
 *     cross-origin child frame.
 *   - After injection, interaction is RENDERER ↔ IFRAME over
 *     `postMessage`:
 *       renderer → iframe : arm / disarm   (pause / resume that screen)
 *       iframe → renderer : ready / selection
 *
 * The inspector owns ALL in-screen interaction (hover highlight, click
 * to select an element, double-click to drill deeper, drag to marquee
 * an area, and the "Add to chat" affordance). It only crosses the
 * boundary on two occasions: `ready` (handshake) and `selection` (the
 * user clicked "Add to chat"). The renderer then captures the pixels
 * and routes the selection to chat.
 *
 * Why DOM descriptors, not scene-node ids: the node ids live only in
 * the game's render code, and stamping them into the DOM is a
 * created-game change we explicitly avoid. So an element selection
 * reports a generic DOM descriptor (tag, visible text, classes,
 * geometry) and the agent localises by grepping the visible text + the
 * crop.
 *
 * This module is the PURE contract: message shapes, builders, type
 * guards, and the origin/source trust predicate. No DOM, no
 * postMessage. Unit-tested (`__tests__/inspect-protocol.test.ts`).
 *
 * British spelling throughout (behaviour, colour) to match the sibling
 * bridge module.
 */

export { CRUCIBLE_INSPECT_MARKER } from '@volley/crucible-ipc-types/preview-inspect-constants';
import { CRUCIBLE_INSPECT_MARKER } from '@volley/crucible-ipc-types/preview-inspect-constants';

/**
 * A rectangle in the INSPECTOR's own coordinate space — the iframe
 * document viewport CSS px (what `getBoundingClientRect()` returns
 * inside the iframe). The renderer maps this to surface coords (the
 * screen's logical resolution) for the context block and to window px
 * for capture, using `viewport` as the basis.
 */
export interface InspectRect {
  x: number;
  y: number;
  width: number;
  height: number;
}

/**
 * Generic DOM descriptor of a selected element. Deliberately NOT a
 * scene-node identity (see module doc). Capped/trimmed by the inspector
 * before it crosses the boundary.
 */
export interface ElementDescriptor {
  /** Lowercased tag name, e.g. `button`, `div`, `img`. */
  tag: string;
  /** Trimmed, length-capped visible text (the agent's main grep key). */
  text: string;
  /** Up to a few class names; [] when none. */
  classes: string[];
  /** ARIA role / label when present — secondary grep keys. */
  role?: string;
  ariaLabel?: string;
  /** `img`/`canvas` src or marker so the agent knows it's media. */
  src?: string;
}

// ---------------------------------------------------------------------------
// renderer → iframe (commands)
// ---------------------------------------------------------------------------

export interface InspectArmMessage {
  __crucibleInspect: 'arm';
}
export interface InspectDisarmMessage {
  __crucibleInspect: 'disarm';
}
export type InspectCommand = InspectArmMessage | InspectDisarmMessage;

// ---------------------------------------------------------------------------
// iframe → renderer (events)
// ---------------------------------------------------------------------------

/** Handshake: the inspector is injected and listening. Doubles as the
 *  per-screen capability gate. */
export interface InspectReadyMessage {
  __crucibleInspect: 'ready';
}

/**
 * The user clicked "Add to chat" on a committed selection. `kind`
 * distinguishes an element pick (carries a descriptor) from an area
 * marquee (geometry only). `rect`/`viewport` are in the inspector's
 * iframe-viewport coordinate space; the renderer maps them.
 */
export interface InspectSelectionMessage {
  __crucibleInspect: 'selection';
  kind: 'element' | 'area';
  rect: InspectRect;
  viewport: { width: number; height: number };
  /** Present only for `kind: 'element'`. */
  descriptor?: ElementDescriptor;
  /** Visible text of the whole screen the selection came from — lets the
   *  agent identify WHICH scene/state it is, not just the cropped region. */
  screenText?: string;
}

/**
 * The user pressed Esc with nothing selected — a request to leave
 * select mode entirely. The inspector posts this when its own Esc
 * ladder bottoms out (after drill-out / clear-selection), so Esc
 * dismisses select mode from inside a focused iframe too (the parent
 * only receives Esc when the parent itself has focus).
 */
export interface InspectExitMessage {
  __crucibleInspect: 'exit';
}

export type InspectEvent = InspectReadyMessage | InspectSelectionMessage | InspectExitMessage;

// ---------------------------------------------------------------------------
// Builders (renderer side — commands)
// ---------------------------------------------------------------------------

export function buildArm(): InspectArmMessage {
  return { __crucibleInspect: 'arm' };
}
export function buildDisarm(): InspectDisarmMessage {
  return { __crucibleInspect: 'disarm' };
}

// ---------------------------------------------------------------------------
// Type guards
// ---------------------------------------------------------------------------

function isRecord(v: unknown): v is Record<string, unknown> {
  return typeof v === 'object' && v !== null;
}

/** Narrow an unknown postMessage payload to an inspect EVENT. Defensive:
 *  any malformed field ⇒ rejected, so another document occupying the
 *  iframe can't feed the renderer junk. */
export function parseInspectEvent(data: unknown): InspectEvent | null {
  if (!isRecord(data)) return null;
  const kind = data[CRUCIBLE_INSPECT_MARKER];
  if (kind === 'ready') return { __crucibleInspect: 'ready' };
  if (kind === 'exit') return { __crucibleInspect: 'exit' };
  if (kind === 'selection') {
    const selKind = data.kind;
    if (selKind !== 'element' && selKind !== 'area') return null;
    const rect = parseRect(data.rect);
    const viewport = parseViewport(data.viewport);
    if (!rect || !viewport) return null;
    const descriptor = selKind === 'element' ? parseDescriptor(data.descriptor) : undefined;
    if (selKind === 'element' && !descriptor) return null;
    const screenText = typeof data.screenText === 'string' ? data.screenText : undefined;
    return {
      __crucibleInspect: 'selection',
      kind: selKind,
      rect,
      viewport,
      ...(descriptor ? { descriptor } : {}),
      ...(screenText ? { screenText } : {}),
    };
  }
  return null;
}

function parseRect(v: unknown): InspectRect | null {
  if (!isRecord(v)) return null;
  const { x, y, width, height } = v;
  if (
    typeof x !== 'number' ||
    typeof y !== 'number' ||
    typeof width !== 'number' ||
    typeof height !== 'number' ||
    !Number.isFinite(x) ||
    !Number.isFinite(y) ||
    !Number.isFinite(width) ||
    !Number.isFinite(height) ||
    width <= 0 ||
    height <= 0
  ) {
    return null;
  }
  return { x, y, width, height };
}

function parseViewport(v: unknown): { width: number; height: number } | null {
  if (!isRecord(v)) return null;
  const { width, height } = v;
  if (
    typeof width !== 'number' ||
    typeof height !== 'number' ||
    !Number.isFinite(width) ||
    !Number.isFinite(height) ||
    width <= 0 ||
    height <= 0
  ) {
    return null;
  }
  return { width, height };
}

function parseDescriptor(v: unknown): ElementDescriptor | null {
  if (!isRecord(v)) return null;
  if (typeof v.tag !== 'string' || v.tag.length === 0) return null;
  const text = typeof v.text === 'string' ? v.text : '';
  const classes = Array.isArray(v.classes)
    ? v.classes.filter((c): c is string => typeof c === 'string')
    : [];
  const out: ElementDescriptor = { tag: v.tag, text, classes };
  if (typeof v.role === 'string') out.role = v.role;
  if (typeof v.ariaLabel === 'string') out.ariaLabel = v.ariaLabel;
  if (typeof v.src === 'string') out.src = v.src;
  return out;
}

/**
 * Whether a received `message` event may be trusted as coming from a
 * preview iframe: the message must originate from that iframe's
 * `contentWindow` AND its declared origin (never `'*'`). The renderer
 * MUST call this before acting on any inspect event.
 */
export function isTrustedInspectSource(args: {
  eventSource: MessageEventSource | null;
  eventOrigin: string;
  iframeContentWindow: Window | null;
  frameOrigin: string | null;
}): boolean {
  if (!args.frameOrigin) return false;
  if (!args.iframeContentWindow) return false;
  if (args.eventSource !== args.iframeContentWindow) return false;
  if (args.eventOrigin !== args.frameOrigin) return false;
  return true;
}
